import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';

import LazyHydratedGooglePublisherTagLandscapeAdSlot from 'components/AdSlots/GooglePublisherTagLandscapeAdSlot/LazyHydratedGooglePublisherTagLandscapeAdSlot';
import Layout from 'components/Layout';
import LazyHydration from 'components/LazyHydration';
import OffersCarousel from 'components/OffersCarousel';
import OffersCarouselHeadingContentWithCategoryLinks from 'components/OffersCarousel/HeadingContentWithCategoryLinks';

import useCommentIdFromRouterHash from 'hooks/useCommentIdFromRouterHash';

import { sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import ArticleHeading from './ArticleHeading';
import DynamicallyLoadedCommentsList from './CommentsList/DynamicallyLoadedCommentsList';
import ArticleHead from './Head';
import ArticleMain from './Main';
import ArticleSchema from './Schema';
import Sidebar from './Sidebar';

const Promonews = dynamic(() => import('components/Blog/Promonews'), {
  ssr: false,
});
const RelatedArticles = dynamic(() => import('./RelatedArticles'), {
  ssr: false,
});

const MIN_RELATED_ARTICLES = 6;

const mergeOfferProps = (offer) =>
  Object.values(offer).reduce(
    (previous, current) => ({ ...previous, ...current }),
    {}
  );

const GA_EVENT_CATEGORY = 'blog_post';

const ArticlePage = ({ article, sidebarAds = [] }) => {
  const {
    articleId,
    articleTitle,
    categoryId,
    categoryName,
    categorySlug,
    commentCount,
    related,
  } = article;

  const { isUtmLoaded } = useUtm();
  const { commentId: commentIdFromUrlHash, isValidCommentIdHashAvailable } =
    useCommentIdFromRouterHash();

  const relatedOffersCategoriesWithValidSlug = useMemo(
    () =>
      related.offers
        .map((offer) => offer?.category)
        .filter((category) => category.categorySlug),
    []
  );

  const relatedOffersSubcategoriesWithValidSlug = useMemo(
    () =>
      related.offers
        .map((offer) => offer?.subcategory)
        .filter((subcategory) => subcategory.subcategorySlug),
    []
  );

  useEffect(() => {
    if (isUtmLoaded) {
      sendSupixEvent('/bl', {
        id: articleId,
        cid: categoryId,
      });
    }
  }, [articleId, categoryId, isUtmLoaded]);

  return (
    <>
      <ArticleHead {...article} />
      <ArticleSchema {...article} />
      <Layout
        gridProps={{ className: 'mb-14' }}
        headingComponent={
          <ArticleHeading
            articleTitle={articleTitle}
            categoryName={categoryName}
            categorySlug={categorySlug}
          />
        }
        sidebarComponent={
          <Sidebar
            gaEventCategory={GA_EVENT_CATEGORY}
            sidebarAds={sidebarAds}
          />
        }
        maxWidthOnMobileDevices
      >
        <ArticleMain gaEventCategory={GA_EVENT_CATEGORY} {...article} />
        <LazyHydratedGooglePublisherTagLandscapeAdSlot
          adId="blog_article_page_main_content_1_320x50_468x60_728x90"
          adPath="/promobit.com.br_blog_article_page/main_content_1_320x50_468x60_728x90"
          className="mb-4 min-h-12 md:min-h-16"
        />
        {related.offers?.length > 0 && (
          <LazyHydration
            force={isValidCommentIdHashAvailable}
            placeholderSize={{ height: 313 }}
          >
            <OffersCarousel
              className="mb-6"
              gaEventCategory={GA_EVENT_CATEGORY}
              headingProps={{
                children: (
                  <OffersCarouselHeadingContentWithCategoryLinks
                    categories={relatedOffersCategoriesWithValidSlug}
                    subcategories={relatedOffersSubcategoriesWithValidSlug}
                  />
                ),
              }}
              offers={related.offers.map(mergeOfferProps)}
            />
          </LazyHydration>
        )}
        {article?.relatedArticles?.length >= MIN_RELATED_ARTICLES && (
          <RelatedArticles
            gaEventCategory={GA_EVENT_CATEGORY}
            relatedArticles={article.relatedArticles}
          />
        )}
        <LazyHydration
          force={isValidCommentIdHashAvailable}
          placeholderSize={{ height: 299 }}
        >
          <Promonews />
        </LazyHydration>
        <DynamicallyLoadedCommentsList
          articleCommentsCount={commentCount}
          articleId={articleId}
          commentIdFromUrlHash={commentIdFromUrlHash}
        />
      </Layout>
    </>
  );
};

ArticlePage.propTypes = {
  article: PropTypes.shape({
    articleDescription: PropTypes.string.isRequired,
    articleId: PropTypes.number.isRequired,
    articleImage: PropTypes.string.isRequired,
    articleModifiedDate: PropTypes.string.isRequired,
    articlePublished: PropTypes.string.isRequired,
    articleSlug: PropTypes.string.isRequired,
    articleTags: PropTypes.arrayOf(
      PropTypes.shape({
        tagId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        tagName: PropTypes.string.isRequired,
        tagSlug: PropTypes.string.isRequired,
      })
    ).isRequired,
    articleText: PropTypes.string.isRequired,
    articleThumbnail: PropTypes.string.isRequired,
    articleTitle: PropTypes.string.isRequired,
    categoryId: PropTypes.string,
    categoryName: PropTypes.string.isRequired,
    categorySlug: PropTypes.string.isRequired,
    commentCount: PropTypes.number.isRequired,
    metaDescription: PropTypes.string.isRequired,
    metaTitle: PropTypes.string.isRequired,
    related: PropTypes.shape({
      articles: PropTypes.arrayOf(
        PropTypes.shape({
          data: PropTypes.shape({
            articleDescription: PropTypes.string.isRequired,
            articleId: PropTypes.number.isRequired,
            articleImage: PropTypes.string.isRequired,
            articleModifiedDate: PropTypes.string.isRequired,
            articlePublished: PropTypes.string.isRequired,
            articleSlug: PropTypes.string.isRequired,
            articleThumbnail: PropTypes.string.isRequired,
            articleTitle: PropTypes.string.isRequired,
            commentCount: PropTypes.number.isRequired,
          }).isRequired,
          meta: PropTypes.shape({
            metaDescription: PropTypes.string.isRequired,
            metaTitle: PropTypes.string.isRequired,
          }).isRequired,
          user: PropTypes.shape({
            userDescription: PropTypes.string.isRequired,
            userName: PropTypes.string.isRequired,
            userPhoto: PropTypes.string.isRequired,
            userUsername: PropTypes.string.isRequired,
          }).isRequired,
        })
      ).isRequired,
      offers: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.shape({
            categoryId: PropTypes.number.isRequired,
            categoryName: PropTypes.string.isRequired,
            categorySlug: PropTypes.string.isRequired,
          }).isRequired,
          data: PropTypes.shape({
            key: PropTypes.string.isRequired,
            offerComments: PropTypes.number.isRequired,
            offerId: PropTypes.number.isRequired,
            offerTitle: PropTypes.string.isRequired,
            offerIsHighlight: PropTypes.bool,
            ratings: PropTypes.shape().isRequired,
            offerOldPrice: PropTypes.number,
            offerPhoto: PropTypes.string.isRequired,
            offerPrice: PropTypes.number.isRequired,
            offerPriceType: PropTypes.string.isRequired,
            offerPublished: PropTypes.string.isRequired,
            offerSlug: PropTypes.string.isRequired,
            offerTags: PropTypes.arrayOf(
              PropTypes.shape({
                name: PropTypes.string.isRequired,
                type: PropTypes.string.isRequired,
              })
            ).isRequired,
          }).isRequired,
          store: PropTypes.shape({
            storeDomain: PropTypes.string.isRequired,
            storeId: PropTypes.number.isRequired,
            storeImage: PropTypes.string.isRequired,
            storeName: PropTypes.string.isRequired,
          }).isRequired,
          subcategory: PropTypes.shape({
            subcategoryId: PropTypes.number,
            subcategoryName: PropTypes.string,
            subcategorySlug: PropTypes.string,
          }).isRequired,
          user: PropTypes.shape({
            userId: PropTypes.number.isRequired,
            userName: PropTypes.string.isRequired,
          }).isRequired,
        })
      ).isRequired,
      offersTitle: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    relatedArticles: PropTypes.arrayOf(
      PropTypes.shape({
        articleDescription: PropTypes.string.isRequired,
        articleId: PropTypes.number.isRequired,
        articleImage: PropTypes.string.isRequired,
        articleModifiedDate: PropTypes.string.isRequired,
        articlePublished: PropTypes.string.isRequired,
        articleSlug: PropTypes.string.isRequired,
        articleThumbnail: PropTypes.string.isRequired,
        articleTitle: PropTypes.string.isRequired,
        commentCount: PropTypes.number.isRequired,
        metaDescription: PropTypes.string.isRequired,
        metaTitle: PropTypes.string.isRequired,
        userDescription: PropTypes.string.isRequired,
        userName: PropTypes.string.isRequired,
        userPhoto: PropTypes.string.isRequired,
        userUsername: PropTypes.string.isRequired,
      })
    ).isRequired,
    topOffers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ).isRequired,
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeImage: PropTypes.string.isRequired,
        storeName: PropTypes.string.isRequired,
        userId: PropTypes.number.isRequired,
        userName: PropTypes.string.isRequired,
        userPhoto: PropTypes.string.isRequired,
        userTypeName: PropTypes.string.isRequired,
        userUsername: PropTypes.string.isRequired,
      })
    ).isRequired,
    userDescription: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    userPhoto: PropTypes.string.isRequired,
    userUsername: PropTypes.string.isRequired,
  }).isRequired,
  sidebarAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      visibility: PropTypes.string.isRequired,
    })
  ),
};

export default ArticlePage;
